import { useState } from 'react';

import {
  Plus,
  MessageSquare,
  Trash2,
  Pencil,
  Clock,
  Eye,
  Power,
  PowerOff,
  Mail,
  Zap,
} from 'lucide-react';

import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  useSmsActionConfigs,
  useCreateSmsActionConfig,
  useUpdateSmsActionConfig,
  useDeleteSmsActionConfig,
} from '@/hooks/useSmsActionConfigs';
import { extractValidationErrors, extractErrorMessage } from '@/lib/utils';

import { SmsActionConfigModal } from './SmsActionConfigModal';
import { SmsActionLogs } from './SmsActionLogs';

import type { SmsActionConfig, CreateSmsActionConfigDto } from '@repo/shared-types';

const MESSAGE_TYPE_LABELS: Record<string, string> = {
  FOLLOWUP_SMS: 'Follow-up SMS',
  MISSED_CALL_SMS: 'Missed Call SMS',
};

const ACTION_TYPE_LABELS: Record<string, string> = {
  LLM_CONFIRMATION: 'LLM Confirmation',
};

export const SmsActionConfigsPage = () => {
  usePageTitle('SMS Actions');
  const { data: configs = [], isLoading, error } = useSmsActionConfigs();
  const createMutation = useCreateSmsActionConfig();
  const updateMutation = useUpdateSmsActionConfig();
  const deleteMutation = useDeleteSmsActionConfig();

  const [editingConfig, setEditingConfig] = useState<SmsActionConfig | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SmsActionConfig | null>(null);
  const [viewLogsConfigId, setViewLogsConfigId] = useState<string | null>(null);
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleNewConfig = () => {
    setEditingConfig(null);
    setIsModalOpen(true);
  };

  const handleEditConfig = (config: SmsActionConfig) => {
    setEditingConfig(config);
    setIsModalOpen(true);
  };

  const handleModalOpenChange = (open: boolean) => {
    setIsModalOpen(open);
    if (!open) setEditingConfig(null);
  };

  const handleSubmitConfig = async (values: CreateSmsActionConfigDto) => {
    try {
      setIsSaving(true);
      if (editingConfig) {
        await updateMutation.mutateAsync({ id: editingConfig.id, data: values });
        toast({
          variant: 'success',
          title: 'Config updated',
          description: `"${values.name}" has been updated successfully.`,
        });
      } else {
        await createMutation.mutateAsync(values);
        toast({
          variant: 'success',
          title: 'Config created',
          description: `"${values.name}" has been created successfully.`,
        });
      }
      setEditingConfig(null);
      setIsModalOpen(false);
    } catch (error) {
      const validationErrors = extractValidationErrors(error);
      if (validationErrors.length > 0) {
        toast({
          variant: 'destructive',
          title: 'Validation error',
          description: validationErrors.map((e) => e.message).join(', '),
        });
      } else {
        toast({
          variant: 'destructive',
          title: editingConfig ? 'Failed to update config' : 'Failed to create config',
          description: extractErrorMessage(error),
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setIsDeleting(true);
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
      toast({
        variant: 'success',
        title: 'Config deleted',
        description: `"${deleteTarget.name}" has been deleted successfully.`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete config',
        description: extractErrorMessage(error),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (viewLogsConfigId) {
    return <SmsActionLogs configId={viewLogsConfigId} onBack={() => setViewLogsConfigId(null)} />;
  }

  if (error) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <MessageSquare className="text-destructive mx-auto h-12 w-12 opacity-50" />
          <p className="text-destructive mt-4 font-medium">Failed to load SMS action configs</p>
          <p className="text-muted-foreground mt-2 text-sm">{extractErrorMessage(error)}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <div className="border-primary mx-auto h-12 w-12 animate-spin rounded-full border-b-2" />
          <p className="text-muted-foreground mt-4">Loading SMS action configs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between">
        <div className="space-y-2">
          <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">SMS Actions</p>
          <h1 className="text-3xl font-bold tracking-tight">SMS Action Configurations</h1>
          <p className="text-muted-foreground max-w-3xl">
            Configure automated actions triggered by SMS replies. Set up LLM-based confirmation
            detection for follow-up and missed call messages.
          </p>
        </div>
        <Button onClick={handleNewConfig}>
          <Plus className="h-4 w-4" />
          New Config
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Configurations</CardTitle>
          <CardDescription>
            Manage SMS action rules, campaign targeting, and reply processing settings.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {configs.length === 0 ? (
            <div className="text-muted-foreground flex min-h-[200px] flex-col items-center justify-center space-y-4 py-8 text-center">
              <MessageSquare className="h-12 w-12 opacity-50" />
              <div>
                <p className="font-medium">No SMS action configurations yet</p>
                <p className="text-sm">Create your first config to get started.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {configs.map((config) => (
                <div key={config.id} className="rounded-lg border p-4">
                  <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-semibold">{config.name}</p>
                        {config.is_active ? (
                          <Badge variant="default" className="gap-1">
                            <Power className="h-3 w-3" />
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="gap-1">
                            <PowerOff className="h-3 w-3" />
                            Inactive
                          </Badge>
                        )}
                      </div>
                      <div className="text-muted-foreground mt-1 flex flex-wrap gap-3 text-sm">
                        <span>
                          {MESSAGE_TYPE_LABELS[config.message_type] || config.message_type}
                        </span>
                        <span>·</span>
                        <span>
                          {config.campaign_mode === 'INCLUDE' ? 'Include' : 'Exclude'}{' '}
                          {config.campaigns?.length || 0} campaign(s)
                        </span>
                        <span>·</span>
                        <span>{ACTION_TYPE_LABELS[config.action_type] || config.action_type}</span>
                        <span>·</span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {config.reply_window_minutes}m window
                        </span>
                        {config.notification_emails && config.notification_emails.length > 0 && (
                          <>
                            <span>·</span>
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {config.notification_emails.length} notification email(s)
                            </span>
                          </>
                        )}
                        {config.on_result_handlers && config.on_result_handlers.length > 0 && (
                          <>
                            <span>·</span>
                            <span className="flex items-center gap-1">
                              <Zap className="h-3 w-3" />
                              {config.on_result_handlers.length} handler(s)
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setViewLogsConfigId(config.id)}
                      >
                        <Eye className="h-4 w-4" />
                        Logs
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleEditConfig(config)}>
                        <Pencil className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setDeleteTarget(config)}
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <SmsActionConfigModal
        open={isModalOpen}
        onOpenChange={handleModalOpenChange}
        config={editingConfig}
        onSubmit={handleSubmitConfig}
        isLoading={isSaving}
      />

      <DeleteConfirmationDialog
        open={Boolean(deleteTarget)}
        resourceName="SMS action config"
        resourceIdentifier={deleteTarget?.name}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};
